import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ShieldCheck, ShieldX, ChevronRight } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Dummy data for categories
const dummyCategories = [
  { name: 'Social Media', allowed: false },
  { name: 'Games', allowed: true },
  { name: 'Productivity', allowed: true },
  { name: 'Entertainment', allowed: false },
  { name: 'Education', allowed: true },
  { name: 'News', allowed: true },
  { name: 'Shopping', allowed: false },
  { name: 'Finance', allowed: true },
];

const CategoryList: React.FC = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: 'allowed' | 'blocked' }>();
  const { serial } = useEffectiveSerial();
  const { toast } = useToast();
  
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const isAllowed = type === 'allowed';
  const categories = dummyCategories.filter(c => c.allowed === isAllowed);
  const title = isAllowed ? 'Allowed Categories' : 'Blocked Categories';
  const actionText = isAllowed ? 'block' : 'allow';

  const handleCategoryTap = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setShowConfirmation(true);
  };

  const handleConfirmRequest = () => {
    if (selectedCategory) {
      toast({
        title: "Request Submitted",
        description: `Your request to ${actionText} "${selectedCategory}" has been submitted for review.`,
      });
    }
    setShowConfirmation(false);
    setSelectedCategory(null);
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/${serial}/settings/app-management`)}
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${isAllowed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'} flex items-center justify-center`}>
              {isAllowed ? (
                <ShieldCheck className="w-5 h-5 text-green-600" />
              ) : (
                <ShieldX className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{title}</h1>
              <p className="text-[13px] text-muted-foreground">{categories.length} categories</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">
        <p className="text-[13px] text-muted-foreground mb-4 px-1">
          {isAllowed 
            ? 'Apps in these categories are available for download. Tap a category to request blocking it.'
            : 'Apps in these categories are not available. Tap a category to request access.'}
        </p>

        <div className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
          <div className="divide-y divide-border/50">
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => handleCategoryTap(category.name)}
                className="w-full px-4 py-4 flex items-center justify-between hover:bg-muted/30 active:scale-[0.99] transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${isAllowed ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-[15px] font-medium text-foreground">{category.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-muted-foreground">Request Change</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {categories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No categories in this list</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-2xl bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>Request to {isAllowed ? 'Block' : 'Allow'} Category?</AlertDialogTitle>
            <AlertDialogDescription className="text-[14px]">
              {isAllowed 
                ? `You are requesting to block the "${selectedCategory}" category. Apps in this category will no longer be available for download.`
                : `You are requesting to allow the "${selectedCategory}" category. Apps in this category will become available for download.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmRequest}
              className="rounded-xl bg-primary text-primary-foreground"
            >
              Submit Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoryList;